import { Link, NavLink, Outlet } from "react-router-dom";
import { Moon, Sun, Bookmark, History as HistoryIcon, LayoutGrid, MountainSnow } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import SearchBar from "./SearchBar";
import { Toaster } from "@/components/ui/sonner";
import { StatePickerModal, StatePill, LanguagePill } from "./StatePicker";
import { useT } from "@/lib/i18n";

export default function Layout() {
  const { theme, toggle } = useTheme();
  const t = useT();

  const navItem = ({ isActive }) =>
    `flex items-center gap-1.5 px-2.5 h-9 rounded-md text-xs font-semibold transition ${
      isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <StatePickerModal />
      <header className="sticky top-0 z-40 border-b border-border/80 backdrop-blur-xl bg-background/85" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center gap-3">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center relative overflow-hidden">
              <MountainSnow className="w-4 h-4 text-primary-foreground relative z-10" strokeWidth={2} />
              <span className="absolute top-0 right-0 w-2 h-2 bg-saffron rounded-bl-sm" />
            </div>
            <div className="hidden sm:block leading-none">
              <div className="text-[15px] font-black tracking-tight">LandCalc</div>
              <div className="overline mt-0.5">India</div>
            </div>
          </Link>

          <div className="flex-1 max-w-xl hidden md:block">
            <SearchBar />
          </div>

          <nav className="flex items-center gap-1 ml-auto">
            <StatePill />
            <LanguagePill />
            <NavLink to="/" end className={navItem} data-testid="nav-dashboard">
              <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
              <span className="hidden xl:inline">{t("dashboard")}</span>
            </NavLink>
            <NavLink to="/favorites" className={navItem} data-testid="nav-favorites">
              <Bookmark className="h-4 w-4" strokeWidth={1.75} />
              <span className="hidden xl:inline">{t("favorites")}</span>
            </NavLink>
            <NavLink to="/history" className={navItem} data-testid="nav-history">
              <HistoryIcon className="h-4 w-4" strokeWidth={1.75} />
              <span className="hidden xl:inline">{t("history")}</span>
            </NavLink>
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              data-testid="theme-toggle-btn"
              className="ml-1 h-9 w-9 rounded-md border border-border bg-card hover:bg-accent transition flex items-center justify-center"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
            </button>
          </nav>
        </div>
        <div className="md:hidden px-4 pb-3">
          <SearchBar compact />
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 print-container" data-testid="main-content">
        <Outlet />
      </main>

      <footer className="border-t border-border/60 mt-8 no-print" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} LandCalc India · State-aware · Values are estimates.</div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-saffron"></span>
            <span>Built for every Indian state</span>
          </div>
        </div>
      </footer>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
